import { ResolveFn, Router, RedirectCommand } from '@angular/router';
import { inject, PLATFORM_ID, TransferState, makeStateKey } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { timeout, catchError } from 'rxjs/operators';
import { firstValueFrom, throwError } from 'rxjs';
import { TenantService } from '../tenant/tenant';
import { API_BASE_URL } from '../config/api-config';

import { UsStatesService } from '../services/us-states.service';
import { US_STATE_CODES } from '../constants/us-states';

export const pageDataResolver: ResolveFn<any> = async (route, state) => {
    const http = inject(HttpClient);
    const tenantService = inject(TenantService);
    const router = inject(Router);
    const apiBaseUrl = inject(API_BASE_URL);
    const usStatesService = inject(UsStatesService);

    // Inject dependencies for manual caching
    const transferState = inject(TransferState);
    const platformId = inject(PLATFORM_ID);

    const domain = tenantService.getDomain();
    const path = route.url.join('/') || 'home';

    const stateCode = route.params['stateCode'] || route.url[0]?.path?.toLowerCase();
    if (stateCode && US_STATE_CODES.has(stateCode)) {
        await usStatesService.loadStateDetails(stateCode);
    }

    // ====================================================================
    // FIX 2: PREVENT DOUBLE EXECUTION USING TRANSFER STATE
    // ====================================================================
    // Create a unique cache key based on the current domain and path
    const stateKey = makeStateKey<any>(`page-data-${domain}-${path}`);

    // If we are in the browser and the server already fetched this, use the cache!
    if (transferState.hasKey(stateKey)) {
        const cachedData = transferState.get(stateKey, null);
        transferState.remove(stateKey); // Clean up memory
        return cachedData; // Instantly return, skipping ALL network requests
    }

    try {

        // ====================================================================
        // FIX 1: PROPER ERROR PROPAGATION FOR THE 404 ROUTE
        // ====================================================================
        console.log(path);
        
        // If the path is 'order', skip the API request to let the client render it
        if (path === 'order') {
            return { layout: null, widgets: {} };
        }

        const stateCode = route.params['stateCode'];
        const slug = route.params['slug'];
        let dashboradPath = stateCode && slug ? `stateApp_${slug}` : path.replace(/\//g, "_");
        
        const pageContent = await firstValueFrom(
            http.get<any>(`${apiBaseUrl}/internal-api/document-content/${domain}/${dashboradPath}`).pipe(
                timeout(5000),
                catchError(error => {
                    console.error(`API failed or timed out for ${domain} at ${path}:`, error.message);
                    // Throw the error so the outer catch block catches it and fires the 404!
                    return throwError(() => error); 
                })
            )
        );


        const widgetData: any = {};
        const pendingRequests = [];

        // ====================================================================
        // FIX 3: BULLETPROOF WIDGET REQUESTS (FAIL SILENTLY)
        // ====================================================================
        // if (pageContent.flags?.includes('pricing')) {
        //     const pricingReq = firstValueFrom(http.get(`${apiBaseUrl}/internal-api/cache/${domain}/pricing`))
        //         .then(data => widgetData.pricing = data)
        //         .catch(err => console.error('Pricing widget failed, but page will still load', err)); // Fails silently
        //     pendingRequests.push(pricingReq);
        // }

        // if (pageContent.flags?.includes('testimonials')) {
        //     const testimonialReq = firstValueFrom(http.get(`${apiBaseUrl}/internal-api/cache/${domain}/testimonials`))
        //         .then(data => widgetData.testimonials = data)
        //         .catch(err => console.error('Testimonials widget failed', err)); // Fails silently
        //     pendingRequests.push(testimonialReq);
        // }

        // if (pendingRequests.length > 0) {
        //     await Promise.all(pendingRequests);
        // }

        const finalData = {
            layout: pageContent || null,
            widgets: widgetData
        };

        // ====================================================================
        // SAVE TO CACHE (ONLY ON THE SERVER)
        // ====================================================================
        if (isPlatformServer(platformId)) {
            transferState.set(stateKey, finalData);
        }

        return finalData;

    } catch (error) {
        // Content fetch failed — redirect to the 404 page
        console.warn('Page content not found or API failure — redirecting to 404.');
        return new RedirectCommand(router.parseUrl('/404'));
    }
};